import { Check } from 'lucide-react';

import BottomSheet from '@/components/BottomSheet';
import { useTranslation } from '@/utils/i18n';
import type { LanguageType } from '@/types/user';

/** Each language written in itself: you recognise your own language. */
const LANGUAGES: { code: LanguageType; label: string }[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
];

/**
 * language-sheet — Profile → Language, as a bottom sheet like the notification
 * settings. A check, not a switch: a switch says on/off, this is a choice among
 * several.
 */
export default function LanguageSheet({
  open,
  current,
  onPick,
  onClose,
}: {
  open: boolean;
  current: LanguageType | string | undefined;
  onPick: (language: LanguageType) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('profile.language')}
      testid="sheet-language"
    >
      <div className="tw:flex tw:flex-col tw:gap-2">
        {LANGUAGES.map(({ code, label }) => {
          const active = current === code;
          return (
            <button
              key={code}
              type="button"
              aria-pressed={active}
              onClick={() => {
                // One choice, then done — unlike the notification settings,
                // which stay open because several switches are set there.
                if (!active) onPick(code);
                onClose();
              }}
              data-testid={`lang-option-${code}`}
              className="tw:flex tw:min-h-16 tw:shrink-0 tw:items-center tw:gap-3 tw:rounded-2xl tw:bg-dark-primary tw:px-4 tw:py-3 tw:text-left tw:text-base tw:text-white"
            >
              <span className="tw:flex-1">{label}</span>
              {active && (
                <Check
                  size={20}
                  className="tw:text-yellow-primary"
                  data-testid={`lang-check-${code}`}
                />
              )}
            </button>
          );
        })}
      </div>
    </BottomSheet>
  );
}
